import { checkoutAccommodationOfferView } from './accommodation'
import { hasExperienceInflowBundleComposition, hasValidExperienceInflowBundleMetadata } from './experienceInflowBundle'
import { createSelector } from 'reselect'
import getExperienceItemsView from './itemViews/getExperienceItemsView'
import { checkoutWithMemberPrice } from './luxPlusSubscription'
import { isExperienceItem } from 'lib/checkout/checkoutUtils'
import { hasValidExperienceUpsellItems } from 'checkout/lib/experiences/experienceUpsell'

export const getExperienceUpsellPricingKey = createSelector(
  (state: App.State) => state.checkout.cart,
  getExperienceItemsView,
  checkoutWithMemberPrice,
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => state.checkout.cart.experienceUpsellBenefit?.kind === 'inflow-bundle' ? checkoutAccommodationOfferView(state) : undefined,
  (cart, views, memberPrice, currentRegion, memberId, accommodation) => JSON.stringify({
    cartId: cart.cartId,
    isGift: cart.isGift,
    mode: cart.mode,
    currency: cart.currencyCode,
    region: cart.regionCode,
    currentRegion,
    memberId,
    campaign: cart.experienceUpsellBenefit?.codeName,
    kind: cart.experienceUpsellBenefit?.kind,
    alternative: cart.experienceUpsellBenefit?.alternative,
    items: cart.items,
    memberPrice,
    ready: views.hasRequiredData,
    accommodationReady: accommodation?.hasRequiredData,
    accommodationPrices: accommodation?.data.map(view => view.itemViews.map(item => item.totals)),
    prices: views.data.map(view => view.ticketViews.map(ticket => [ticket.item.transactionKey, ticket.ticket.price, ticket.ticket.memberPrice])),
  }),
)

export function hasExperienceUpsellComposition(state: App.State, kind = state.checkout.cart.experienceUpsellBenefit?.kind): boolean {
  if (kind === 'inflow-bundle') return hasExperienceInflowBundleComposition(state)
  const experiences = state.checkout.cart.items.filter(isExperienceItem)
  return hasValidExperienceUpsellMetadata(state) && experiences.some(item => item.metaData?.checkoutExperienceUpsell === true) &&
    experiences.some(item => item.metaData?.checkoutExperienceUpsell !== true)
}

export function hasValidExperienceUpsellMetadata(state: App.State): boolean {
  return hasValidExperienceUpsellItems(state.checkout.cart.items) && hasValidExperienceInflowBundleMetadata(state)
}

export function isExperienceUpsellPricingReady(state: App.State): boolean {
  const benefit = state.checkout.cart.experienceUpsellBenefit
  const promotion = state.checkout.promo
  const composition = hasExperienceUpsellComposition(state)
  const hasAlternative = !!benefit?.alternative.promoCode || !!benefit?.alternative.voucherIds.length
  const requiresPricing = !!promotion?.experienceUpsellPricingKey || (composition && (benefit?.choice === 'upsell' || (benefit?.choice === 'existing' && hasAlternative)))
  if (!requiresPricing) return true
  const request = state.checkout.experienceUpsellRequest
  const activeBenefitFailed = request?.status === 'error' && request.choice === benefit?.choice
  return promotion?.experienceUpsellPricingKey === getExperienceUpsellPricingKey(state) && request?.status !== 'loading' && !activeBenefitFailed
}

export function getAppliedExperienceInflowBundleSavings(state: App.State): number {
  return state.checkout.cart.experienceUpsellBenefit?.kind === 'inflow-bundle' &&
    state.checkout.cart.experienceUpsellBenefit.choice === 'upsell' && hasExperienceInflowBundleComposition(state) &&
    isExperienceUpsellPricingReady(state) && state.checkout.promo?.experienceUpsellPricingKey === getExperienceUpsellPricingKey(state)
    ? state.checkout.promo.discountTotal ?? 0 : 0
}
